import { useRef, useState } from 'react';

import { SubmitButton } from '@capsule/consumer-design-system';

import { useMountEffect } from '@react-hookz/web';
import { Form } from 'formik';
import styled, { css } from 'styled-components';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Card, CardItem } from 'src/app/components/Elements/Card';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import Spinner from 'src/app/components/Elements/Spinner';
import { SegmentFormIncomplete } from 'src/app/components/Error/SegmentFormIncomplete';
import { useFocusOnFirstFormError } from 'src/app/components/Form/Hooks/useFocusOnFirstFormError';
import { MedicationProviderFields } from 'src/app/components/Form/MedicationFields/MedicationProviderFields';
import TextInput from 'src/app/components/Form/TextInput';
import ToggleInput from 'src/app/components/Form/ToggleInput';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { useSegmentTrack } from 'src/app/hooks/useSegmentTrack';
import { getAccount, getAccountIsFetching } from 'src/app/state/selectors';
import { useSelector } from 'src/app/state/store';

const StyledCard = styled(Card)`
  &&& > * {
    padding-top: ${({ theme }) => theme.space.s3};
    padding-bottom: ${({ theme }) => theme.space.s3};
  }

  &&& > *:first-child {
    padding-top: ${({ theme }) => theme.space.s5};
    padding-bottom: ${({ theme }) => theme.space.s3};
  }

  &&& > *:last-child {
    padding-top: ${({ theme }) => theme.space.s3};
    padding-bottom: ${({ theme }) => theme.space.s5};
  }
`;

const StyledToggleInput = styled(ToggleInput)`
  &&& {
    margin-bottom: 0;
  }

  &&& + * label {
    margin-top: ${({ theme }) => theme.space.s4};
  }
`;

const YES = 'Yes';
const NO = 'No';

const toggleValues = [
  { value: YES, label: YES },
  { value: NO, label: NO },
];

const AllergyProfile = ({ isSubmitting, values, errors, isValidating, dispatchLoadAccount, setFieldValue }) => {
  const [hasAllergies, setHasAllergies] = useState(false);
  const [hasMedicalConditions, setHasMedicalConditions] = useState(false);
  const [hasMedications, setHasMedications] = useState(false);
  const dispatchSelectCheckoutMedicalProfileToggle = useSegmentEvent('selectCheckoutMedicalProfileToggle', {
    shouldTrackOnMount: false,
  });

  const account = useSelector(getAccount);
  const isAccountFetching = useSelector(getAccountIsFetching);

  useMountEffect(() => {
    if (!isAccountFetching && !account) {
      dispatchLoadAccount();
    }
  });

  useSegmentTrack(UNIFIED.viewCheckoutAllergyProfile);

  const formRef = useRef(null);
  useFocusOnFirstFormError(formRef, errors, isSubmitting, isValidating, false);

  const handleClick = (field: string, value: string) => {
    dispatchSelectCheckoutMedicalProfileToggle({ field, value });

    if (value === NO) {
      if (field === 'medications') {
        setFieldValue('medications', []);
      } else {
        setFieldValue(field, '');
      }
    } else if (value === YES) {
      if (field === 'medications') {
        setFieldValue('medications', ['']);
      }
    }
  };

  if (isAccountFetching) {
    return (
      <StandardPage title={'Your pharmacist wants to know'}>
        <Spinner centered />
      </StandardPage>
    );
  }

  return (
    <StandardPage title={'Your pharmacist wants to know'}>
      <Form ref={formRef} noValidate>
        {/**
         * @TODO Kevin Wang
         * The Segment event here fires 4 times. Figure out how to
         * suppress this with a `usePrevious` hook & formik.submitCount
         */}
        <SegmentFormIncomplete />

        <StyledCard>
          <CardItem noDivider>
            <StyledToggleInput
              name="allergiesToggle"
              label="Do you have any allergies?"
              trackClick={value => {
                setHasAllergies(value === YES);
                handleClick('allergies', value);
              }}
              options={toggleValues}
            />
            {(values.allergies || hasAllergies) && <TextInput name="allergies" label="Allergies" />}
          </CardItem>

          <CardItem noDivider>
            <StyledToggleInput
              name="conditionsToggle"
              label="Do you have any medical conditions?"
              trackClick={value => {
                setHasMedicalConditions(value === YES);
                handleClick('conditions', value);
              }}
              options={toggleValues}
            />
            {(values.conditions || hasMedicalConditions) && <TextInput name="conditions" label="Medical Conditions" />}
          </CardItem>

          <CardItem noDivider>
            <StyledToggleInput
              name="medicationsToggle"
              label="Are you taking any other medications?"
              trackClick={value => {
                setHasMedications(value === YES);
                handleClick('medications', value);
              }}
              options={toggleValues}
            />

            {(values.medications?.[0] || hasMedications) && <MedicationProviderFields />}
          </CardItem>
        </StyledCard>

        <SubmitButton
          isSubmitting={isSubmitting}
          variant="outline-lg"
          isFullWidth
          css={css`
            display: block;
            margin-top: ${({ theme }) => theme.space.s3};
          `}
        >
          Save
        </SubmitButton>
      </Form>
    </StandardPage>
  );
};

export default AllergyProfile;
